import type { Consent } from '@medplum/fhirtypes';
import { z } from 'zod';
import { CONSENT_DOCUMENT_VERSION_IDENTIFIER_SYSTEM } from './onboardingConsent';

/**
 * The patient's decision about sending their data to the third-party AI service that writes Liza's
 * chat replies (#8694, Apple 5.1.1(i) / 5.1.2(i)).
 *
 * Deliberately **not** a fourth entry in `ONBOARDING_CONSENT_LINK_IDS`: adding one there would put
 * every already-onboarded patient back through re-consent before they could open any screen at all.
 * This is chat-scoped, declinable, and revocable, so it gets its own category system — which also
 * keeps it invisible to `grantedOnboardingConsentCategoryCodes`, whose filter keys on the onboarding
 * system.
 */
export const AI_DATA_SHARING_CONSENT_CATEGORY_SYSTEM = 'https://liza.health/fhir/codesystem/ai-data-sharing-consent';
export const AI_DATA_SHARING_CONSENT_CATEGORY_CODE = 'ai-data-sharing';
export const AI_DATA_SHARING_CONSENT_POLICY_URI = 'https://liza.health/fhir/policy/ai-data-sharing-consent';

/** FHIR search token for the category, for reads that filter server-side. */
export const AI_DATA_SHARING_CONSENT_CATEGORY_TOKEN = `${AI_DATA_SHARING_CONSENT_CATEGORY_SYSTEM}|${AI_DATA_SHARING_CONSENT_CATEGORY_CODE}`;

/**
 * The `DocumentReference` identifier value for the in-app AI data-sharing disclosure — the
 * `definition` token `getAiDataSharingDisclosureDocument` resolves, the same role a questionnaire
 * item's `linkId` plays for `buildOnboardingConsent`'s documents.
 *
 * The disclosure is a `DocumentReference` (not app-copy) so a patient's agreement points at the
 * literal wording they read, the same evidentiary shape as the onboarding consents, and so legal's
 * usual document-review/deploy pipeline is where this text is authored and changed.
 */
export const AI_DATA_SHARING_DISCLOSURE_DEFINITION = 'ai-data-sharing-disclosure';

/**
 * The disclosure body, authored in `apps/medplum-config` and stored as a JSON attachment on the
 * `DocumentReference` — not HTML like the onboarding consents. patient-native has no HTML renderer
 * (a native port of the onboarding consent flow is separately tracked, #6769) and this screen's
 * sections (what's sent / who / choice) are structured content the client renders directly, not a
 * wall of legal prose, so JSON is both simpler and requires no new native dependency.
 *
 * Validated at read time (`getAiDataSharingDisclosureDocument`) rather than trusted, since it comes
 * off a base64 attachment rather than a compiler-checked import: malformed content degrades to "no
 * document," which the gate treats as `error` — never as permission.
 *
 * Sections legal may add or drop are optional here, for two reasons: a client running ahead of a
 * `medplum-config` deploy should still render the live document rather than fail validation and gate
 * chat behind `error`, and which optional sections the approved wording carries is a copy decision
 * (#8724) rather than something this schema should force. The required set is what Apple's finding
 * turns on — what is sent, who it goes to, and that there is a choice. Which sections the *current*
 * wording actually carries is asserted at the source
 * (`apps/medplum-config/src/documentReference/__tests__/aiDataSharingDisclosure.test.ts`), which is
 * also where Apple's four requirements are traced to specific lines.
 */
export const AiDataSharingDisclosureContentSchema = z.object({
  title: z.string().min(1),
  intro: z.string().min(1),
  /** Translation runs through the same vendor, so it is disclosed with the send rather than buried in the list. */
  translationNote: z.string().min(1).optional(),
  whatIsSentHeading: z.string().min(1),
  whatIsSent: z.array(z.string().min(1)).min(1),
  /** Absent while chat has no attachments to carve out — see `consent-ai-data-sharing-en.ts`. */
  attachmentsNote: z.string().min(1).optional(),
  whoHeading: z.string().min(1),
  who: z.string().min(1),
  safetyHeading: z.string().min(1).optional(),
  safety: z.array(z.string().min(1)).min(1).optional(),
  choiceHeading: z.string().min(1),
  choice: z.string().min(1),
  choicePoints: z.array(z.string().min(1)).min(1).optional(),
});

export type AiDataSharingDisclosureContent = z.infer<typeof AiDataSharingDisclosureContentSchema>;

/** The disclosure `DocumentReference`, decoded — what a client needs to render it and record a decision against it. */
export type AiDataSharingDisclosureDocument = {
  documentReferenceId: string | undefined;
  /** Value of the document's version identifier (e.g. `ai-data-sharing-disclosure-v1`), stamped onto the `Consent`. */
  version: string | undefined;
  content: AiDataSharingDisclosureContent;
};

/**
 * What the server can tell a client about the gate. `unknown` means never asked — it is **not** a
 * decision, and no client may treat it (or a failed read) as permission.
 */
export type AiDataSharingDecision = 'granted' | 'declined' | 'unknown';

export type AiDataSharingConsentStatus = {
  decision: AiDataSharingDecision;
  /** Disclosure version the resolved decision was recorded against, when one was stamped. */
  version: string | undefined;
  /** `Consent.dateTime` of the resolved decision. */
  decidedAt: string | undefined;
};

/** The document a patient actually read, as returned by `aiDataSharing.getDisclosureDocument`. */
export type SignedAiDataSharingDisclosure = {
  documentReferenceId: string | undefined;
  version: string | undefined;
};

/**
 * The `Consent` recorded when a patient accepts, declines, or later revokes AI data sharing.
 *
 * Every decision appends a new resource rather than mutating the last one, so the history is the
 * audit trail — a revocation must not erase the evidence that the patient once agreed, nor which
 * wording they agreed to. `status` and `provision.type` are set together and always agree: a
 * revoked or refused consent is not in force, so it is `inactive`/`deny`.
 *
 * Shaped like `buildOnboardingConsent`: `identifier` stamps the version, `sourceReference` points at
 * the `DocumentReference` the patient actually read — evidence of *which wording*, not just that
 * some wording existed.
 */
export const buildAiDataSharingConsent = (
  granted: boolean,
  signedDocument?: SignedAiDataSharingDisclosure,
): Omit<Consent, 'resourceType' | 'patient' | 'dateTime'> => ({
  status: granted ? 'active' : 'inactive',
  scope: { coding: [{ system: 'http://terminology.hl7.org/CodeSystem/consentscope', code: 'patient-privacy' }] },
  category: [
    {
      coding: [{ system: AI_DATA_SHARING_CONSENT_CATEGORY_SYSTEM, code: AI_DATA_SHARING_CONSENT_CATEGORY_CODE }],
    },
  ],
  policy: [{ uri: AI_DATA_SHARING_CONSENT_POLICY_URI }],
  provision: { type: granted ? 'permit' : 'deny' },
  ...(signedDocument?.version
    ? { identifier: [{ system: CONSENT_DOCUMENT_VERSION_IDENTIFIER_SYSTEM, value: signedDocument.version }] }
    : {}),
  ...(signedDocument?.documentReferenceId
    ? { sourceReference: { reference: `DocumentReference/${signedDocument.documentReferenceId}` } }
    : {}),
});

/** Whether a `Consent` is one of ours — i.e. carries the AI data-sharing category coding. */
export const isAiDataSharingConsent = (consent: Consent): boolean =>
  (consent.category ?? [])
    .flatMap((category) => category.coding ?? [])
    .some(
      (coding) =>
        coding.system === AI_DATA_SHARING_CONSENT_CATEGORY_SYSTEM &&
        coding.code === AI_DATA_SHARING_CONSENT_CATEGORY_CODE,
    );

const versionOf = (consent: Consent): string | undefined =>
  (consent.identifier ?? []).find((identifier) => identifier.system === CONSENT_DOCUMENT_VERSION_IDENTIFIER_SYSTEM)
    ?.value;

/**
 * The patient's current decision, given every `Consent` they hold and the disclosure's current live
 * version (from `getAiDataSharingDisclosureDocument` — the `DocumentReference` is upserted in place
 * on deploy, so "current" is always a live read, never a fixed list).
 *
 * Newest `dateTime` wins, because a decision is appended rather than edited — that is what makes
 * revoking from Profile take effect and what makes changing your mind again after that work too.
 *
 * Three outcomes, and the difference between the two negative ones is what the client does next:
 * - `granted` — an `active`/`permit` decision stamped with the disclosure's current version.
 * - `declined` — an explicit refusal or revocation. Chat stays unavailable and the patient is *not*
 *   re-prompted; they opt back in from Profile.
 * - `unknown` — never asked, or agreed to wording that has since been superseded (including when
 *   `currentVersion` is undefined because the document itself failed to resolve — a version we can't
 *   confirm is current is not treated as current). The disclosure is presented, because permission
 *   against text we no longer show is not permission for this text.
 */
export const resolveAiDataSharingConsentStatus = (
  consents: Consent[],
  currentVersion: string | undefined,
): AiDataSharingConsentStatus => {
  const decisions = consents.filter(isAiDataSharingConsent);
  if (decisions.length === 0) return { decision: 'unknown', version: undefined, decidedAt: undefined };

  // Undated resources sort oldest: a decision we can't place in time must never outrank a dated one.
  const newest = decisions.reduce((latest, consent) =>
    (consent.dateTime ?? '') > (latest.dateTime ?? '') ? consent : latest,
  );

  const version = versionOf(newest);
  const resolved = { version, decidedAt: newest.dateTime };

  if (newest.status !== 'active' || newest.provision?.type !== 'permit') {
    return { decision: 'declined', ...resolved };
  }
  const isCurrentWording = version !== undefined && currentVersion !== undefined && version === currentVersion;
  return { decision: isCurrentWording ? 'granted' : 'unknown', ...resolved };
};
